import { filter, fromEvent, map, tap } from 'rxjs';

import FullModal from 'utils/components/full-modal';

export default (node: HTMLElement, CLASSES: { HEADER_OVERLAY_ACTIVE: string }) => {
  const topLevelsLinks = node.querySelectorAll<HTMLElement>('[data-link-level="1"]');
  const container = node.querySelector('[data-target="mega-menu"]');
  const linksArray = Array.from(topLevelsLinks);

  const $click = fromEvent(topLevelsLinks, 'click').pipe(
    filter(
      (event) =>
        event.currentTarget instanceof HTMLElement &&
        event.currentTarget.dataset.child !== undefined,
    ),
    tap((event) => {
      event.preventDefault();
    }),
    map((event) => event.currentTarget as HTMLElement),
  );

  let currentActive: {
    link: HTMLElement;
    menu: FullModal;
  } = null;
  const menus = new Map(
    Array.from(container.querySelectorAll<HTMLElement>('[data-menu]')).map((menu) => [
      Array.from(topLevelsLinks).find((link) => link.dataset.child === menu.dataset.menu),
      new FullModal(menu, {
        overlay: {
          index: 20,
        },
      }),
    ]),
  );

  const openMenuForLink = (target: HTMLElement): void => {
    const menu = menus.get(target);

    if (currentActive) {
      currentActive.menu.close();
      currentActive.link.ariaExpanded = 'false';

      if (menu === currentActive.menu) {
        currentActive = null;

        return;
      }
    }

    if (menu) {
      menu.open();
      target.ariaExpanded = 'true';

      currentActive = {
        link: target,
        menu,
      };
    }
  };

  $click.subscribe((target) => {
    openMenuForLink(target);
  });

  const navigateMenubar = (fromLink: HTMLElement, targetIndex: number): void => {
    const target = linksArray[targetIndex];

    if (!target || target === fromLink) {
      return;
    }

    if (currentActive) {
      if (target.dataset.child !== undefined) {
        openMenuForLink(target);
      } else {
        currentActive.menu.close();
        currentActive.link.ariaExpanded = 'false';
        currentActive = null;
        target.focus();
      }
    } else {
      target.focus();
    }
  };

  const handleMenubarArrowKey = (event: KeyboardEvent, fromLink: HTMLElement): void => {
    const currentIndex = linksArray.indexOf(fromLink);

    if (currentIndex === -1) {
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      const next = (currentIndex + 1) % linksArray.length;

      navigateMenubar(fromLink, next);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      const prev = (currentIndex - 1 + linksArray.length) % linksArray.length;

      navigateMenubar(fromLink, prev);
    } else if (event.key === 'Home') {
      event.preventDefault();
      navigateMenubar(fromLink, 0);
    } else if (event.key === 'End') {
      event.preventDefault();
      navigateMenubar(fromLink, linksArray.length - 1);
    }
  };

  fromEvent(topLevelsLinks, 'keydown').subscribe((event) => {
    if (!(event instanceof KeyboardEvent) || !(event.currentTarget instanceof HTMLElement)) {
      return;
    }

    handleMenubarArrowKey(event, event.currentTarget);
  });

  menus.forEach((menu) => {
    fromEvent(menu.root, 'keydown', { capture: true }).subscribe((event) => {
      if (!(event instanceof KeyboardEvent) || !currentActive || currentActive.menu !== menu) {
        return;
      }

      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      handleMenubarArrowKey(event, currentActive.link);
    });
  });

  menus.forEach((menu) => {
    fromEvent(menu.root.querySelectorAll('[data-close]'), 'click').subscribe((event) => {
      event.preventDefault();

      menu.close();
    });

    menu.on('open', () => {
      node.setAttribute('data-overlay-element', 'navigation');
      node.classList.add(CLASSES.HEADER_OVERLAY_ACTIVE);
      document.body.style.overflow = 'hidden';
    });

    menu.on('close', () => {
      if (currentActive?.menu === menu) {
        currentActive.link.ariaExpanded = 'false';
        currentActive = null;
      }

      if (currentActive === null) {
        node.removeAttribute('data-overlay-element');
        node.classList.remove(CLASSES.HEADER_OVERLAY_ACTIVE);
        document.body.style.overflow = null;
      }
    });
  });
};
